import { MouseEventHandler } from "react";

import { useElementSizeAndScroll } from "@covertree/hooks";
import Grid from "@mui/material/Grid";
import MuiTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import MuiTableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import { colors } from "@covertree/theme/colors";

import { TableLoading } from "../TableLoading";
import { TableRow } from "../TableRow";
import { Column, Row } from "../types";

interface TableProps {
  id?: string;
  columns: Column[];
  rows: Row[];
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortChange?: (column: string) => MouseEventHandler;
  loading?: boolean;
  error?: boolean;
  capitalizeRow?: boolean;
  onRowActionClick?: () => void;
  onTableRowClick?: () => void;
  maxHeight?: number | string;
  customCellFn?: (columnData: boolean) => JSX.Element;
}
// Note: for customCellFn defined and boolean-typed data for specific column, customeCellFn will be used to render the cell value
export const Table = ({
  id = "table",
  columns,
  rows,
  sortOrder,
  sortBy,
  onSortChange,
  loading,
  error,
  capitalizeRow = true,
  onRowActionClick,
  onTableRowClick,
  maxHeight,
  customCellFn,
}: TableProps) => {
  const theme = useTheme();

  const tableDimensions = useElementSizeAndScroll(
    `#${id}-container`,
    error || loading
  );

  if (loading) {
    return <TableLoading height={theme.spacing(38)} />;
  }

  if (error && !loading)
    return (
      <Grid container justifyContent="center" alignItems="center">
        <Grid item>
          <Typography>
            Could not get any data for table. Please check your internet
            connection or contact our team.
          </Typography>
        </Grid>
      </Grid>
    );

  if (rows.length < 1)
    return (
      <Grid container justifyContent="center" alignItems="center">
        <Grid item>
          <Typography>There are no items to show for this table.</Typography>
        </Grid>
      </Grid>
    );

  return (
    <TableContainer
      id={`${id}-container`}
      sx={{
        maxHeight: maxHeight ?? `calc( 100vh - ${theme.spacing(41)} )`,
        minHeight: theme.spacing(10),
        backgroundImage: "linear-gradient(white, white)",
        backgroundColor: "transparent",
        transition: "background-color 0.3s ease",
        "&:hover": {
          backgroundColor: colors.scrollbar,
          transition: "background-color 0.3s ease",
        },
        "&::-webkit-scrollbar": {
          width: theme.spacing(2),
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "inherit",
          borderRadius: theme.spacing(1),
          border: `${theme.spacing(0.5)} solid transparent`,
          backgroundClip: "content-box",
          transition: "background-color 0.3s ease",
        },
        "&::-webkit-scrollbar-button": {
          width: 0,
          height: 0,
          display: "none",
        },
      }}>
      <MuiTable stickyHeader>
        <TableHead>
          <MuiTableRow>
            {columns.map((column) => (
              <TableCell
                key={column.headerName}
                width={column.width}
                sx={{
                  boxShadow: "none",
                  color: "text.secondary",
                  fontSize: "1rem",
                  lineHeight: 1.25,
                  padding: theme.spacing(1.25),
                  backgroundColor: colors.white,
                  "& > :first-of-type": {
                    borderRadius: theme.spacing(1, 0, 0, 1),
                  },
                  "&:last-child": {
                    paddingRight: theme.spacing(),
                    borderRadius: theme.spacing(0, 1, 1, 0),
                  },
                }}>
                {column.sortBy ? (
                  <TableSortLabel
                    active={column.sortBy === sortBy}
                    direction={sortOrder}
                    onClick={
                      onSortChange ? onSortChange(column.sortBy) : undefined
                    }
                    data-testid={`sortlabel-${column.sortBy}`}>
                    <Typography variant="subtitle1" noWrap>
                      {column.headerName}
                    </Typography>
                  </TableSortLabel>
                ) : (
                  <Typography variant="subtitle1" noWrap>
                    {column.headerName}
                  </Typography>
                )}
              </TableCell>
            ))}
          </MuiTableRow>
        </TableHead>
        <TableBody>
          {!loading &&
            !error &&
            rows.map((row) => (
              <TableRow
                id={`${id}-row-${row.id}`}
                key={row.id}
                row={row}
                columns={columns}
                tableDimensions={tableDimensions}
                capitalize={capitalizeRow}
                onRowActionClick={onRowActionClick}
                onTableRowClick={onTableRowClick}
                customCellFn={customCellFn}
              />
            ))}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};
